// Integration health dashboard.
//
// Hits /admin/integrations/health (IntegrationMonitor.getHealth) and
// renders one row per external service tracked, with last-24h success
// rate, last-30-min error count, average duration, and last error
// message. Polls every 60s to match the top-nav badge cadence so the
// dot and the table never disagree.

import { useQuery } from "@tanstack/react-query";
import { Activity, RefreshCw, CheckCircle2, AlertCircle, XCircle, HelpCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";

interface IntegrationHealth {
  integration: string;
  display_name: string;
  status: "healthy" | "degraded" | "down" | "unknown";
  last_success: string | null;
  last_error: string | null;
  errors_30min: number;
  errors_24h: number;
  total_24h: number;
  success_rate_24h: number;
  avg_duration_ms_24h: number | null;
  last_error_message: string | null;
}

interface HealthResponse {
  rollup: "healthy" | "degraded" | "down";
  integrations: IntegrationHealth[];
}

const STATUS_CONFIG: Record<
  IntegrationHealth["status"],
  { icon: JSX.Element; pill: string; dot: string }
> = {
  healthy: {
    icon: <CheckCircle2 size={14} className="text-emerald-600" />,
    pill: "bg-emerald-100 text-emerald-800",
    dot: "bg-emerald-500",
  },
  degraded: {
    icon: <AlertCircle size={14} className="text-amber-600" />,
    pill: "bg-amber-100 text-amber-800",
    dot: "bg-amber-500",
  },
  down: {
    icon: <XCircle size={14} className="text-red-600" />,
    pill: "bg-red-100 text-red-800",
    dot: "bg-red-500",
  },
  unknown: {
    icon: <HelpCircle size={14} className="text-muted-foreground" />,
    pill: "bg-muted text-muted-foreground",
    dot: "bg-gray-400",
  },
};

function formatRelative(iso: string | null, t: (k: string, f: string) => string): string {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return t("integrations.relSecondsAgo", "{n}s ago").replace("{n}", String(sec));
  const min = Math.round(sec / 60);
  if (min < 60) return t("integrations.relMinutesAgo", "{n}m ago").replace("{n}", String(min));
  const hr = Math.round(min / 60);
  if (hr < 24) return t("integrations.relHoursAgo", "{n}h ago").replace("{n}", String(hr));
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Integrations() {
  const t = useT();

  const { data, isError, isFetching, refetch } = useQuery({
    queryKey: ["admin", "integrations", "health", "page"],
    queryFn: () => api<HealthResponse>("/api/v1/admin/integrations/health"),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  const integrations = data?.integrations ?? [];
  const rollup = data?.rollup;

  return (
    <div className="max-w-6xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
            <Activity size={20} /> {t("integrations.title", "Integrations")}
          </h1>
          <p className="mt-0.5 font-body text-xs text-muted-foreground">
            {t(
              "integrations.subtitle",
              "Health of every external service the API talks to. Updates every 60s.",
            )}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={`mr-1 ${isFetching ? "animate-spin" : ""}`} />
          {t("integrations.refresh", "Refresh")}
        </Button>
      </div>

      {rollup && (
        <div
          className={`flex items-center gap-3 rounded-lg border border-border p-4 ${
            rollup === "healthy"
              ? "bg-emerald-50 dark:bg-emerald-950/30"
              : rollup === "degraded"
                ? "bg-amber-50 dark:bg-amber-950/30"
                : "bg-red-50 dark:bg-red-950/30"
          }`}
        >
          <span className={`size-3 rounded-full ${STATUS_CONFIG[rollup].dot}`} />
          <div className="font-body text-sm">
            <span className="font-semibold capitalize text-foreground">{rollup}</span>{" "}
            <span className="text-muted-foreground">
              ·{" "}
              {rollup === "healthy"
                ? t(
                    "integrations.rollupHealthy",
                    "All tracked integrations are operating normally.",
                  )
                : rollup === "degraded"
                  ? t(
                      "integrations.rollupDegraded",
                      "One or more integrations are showing intermittent errors.",
                    )
                  : t(
                      "integrations.rollupDown",
                      "One or more integrations are failing — investigate now.",
                    )}
            </span>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        {isError ? (
          <div className="p-8 text-center font-body text-sm text-destructive">
            {t("integrations.loadError", "Failed to load integration health.")}
          </div>
        ) : !data ? (
          <div className="p-8 text-center font-body text-sm text-muted-foreground">
            <Loader2 size={16} className="mx-auto mb-2 animate-spin" />
            {t("integrations.loading", "Loading…")}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-bold">{t("integrations.colService", "Service")}</TableHead>
                <TableHead className="font-bold">{t("integrations.colStatus", "Status")}</TableHead>
                <TableHead className="font-bold text-right">
                  {t("integrations.colSuccessRate", "24h success")}
                </TableHead>
                <TableHead className="font-bold text-right">
                  {t("integrations.colTotal24h", "24h calls")}
                </TableHead>
                <TableHead className="font-bold text-right">
                  {t("integrations.colErrors30m", "30m errors")}
                </TableHead>
                <TableHead className="font-bold text-right">
                  {t("integrations.colAvgMs", "Avg ms")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("integrations.colLastSuccess", "Last success")}
                </TableHead>
                <TableHead className="font-bold">
                  {t("integrations.colLastError", "Last error")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {integrations.map((it) => {
                const cfg = STATUS_CONFIG[it.status];
                return (
                  <TableRow key={it.integration} className="hover:bg-muted/30">
                    <TableCell className="font-body text-sm font-medium text-foreground">
                      {it.display_name}
                      <div className="font-body text-[10px] text-muted-foreground">
                        {it.integration}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 font-body text-[11px] font-medium capitalize ${cfg.pill}`}
                      >
                        {cfg.icon}
                        {it.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-body text-sm tabular-nums text-foreground">
                      {it.total_24h === 0 ? "—" : `${it.success_rate_24h}%`}
                    </TableCell>
                    <TableCell className="text-right font-body text-sm tabular-nums text-muted-foreground">
                      {it.total_24h}
                    </TableCell>
                    <TableCell
                      className={`text-right font-body text-sm tabular-nums ${
                        it.errors_30min > 0 ? "font-semibold text-destructive" : "text-muted-foreground"
                      }`}
                    >
                      {it.errors_30min}
                    </TableCell>
                    <TableCell className="text-right font-body text-sm tabular-nums text-muted-foreground">
                      {it.avg_duration_ms_24h ?? "—"}
                    </TableCell>
                    <TableCell className="font-body text-xs text-muted-foreground">
                      {formatRelative(it.last_success, t)}
                    </TableCell>
                    <TableCell className="max-w-[260px] font-body text-xs">
                      {it.last_error ? (
                        <div>
                          <div className="text-muted-foreground">
                            {formatRelative(it.last_error, t)}
                          </div>
                          {it.last_error_message && (
                            <div className="truncate text-destructive" title={it.last_error_message}>
                              {it.last_error_message}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

export default Integrations;
